// The Format menu in the message box and the edit box (#344): the keys, and applying a Format item to the selection.
// The rule is shared/src/formatSelection.ts; this file reads the textarea, runs it and types the result back in.
//   ⌘B bold, ⌘I italic, ⌘U underline, ⌘⇧X strike, ⌘E code (Ctrl on Windows and Linux).
// Spoiler, quote and code block have no key: the likely ones are taken by the browsers and macOS.
import type { KeyboardEvent } from "react";
import { formatSelection, type FormatItem } from "@bloom/shared";

interface Key {
  item: FormatItem;
  /** The letter, lower case. */
  key: string;
  shift: boolean;
}

export const FORMAT_KEYS: readonly Key[] = [
  { item: "bold", key: "b", shift: false },
  { item: "italic", key: "i", shift: false },
  { item: "underline", key: "u", shift: false },
  { item: "strike", key: "x", shift: true },
  { item: "code", key: "e", shift: false },
];

/** Apple's keyboards use ⌘; elsewhere Ctrl. Read each time, so a test can pretend. */
export const isApple = () => typeof navigator !== "undefined" && /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || "");

/** The key for an item as people read it ("⌘B", "Ctrl+Shift+X"), or null for an item with no key. */
export function formatKeyLabel(item: FormatItem): string | null {
  const k = FORMAT_KEYS.find((x) => x.item === item);
  if (!k) return null;
  const letter = k.key.toUpperCase();
  return isApple() ? `⌘${k.shift ? "⇧" : ""}${letter}` : `Ctrl+${k.shift ? "Shift+" : ""}${letter}`;
}

/** The item a key press means, or null. On Apple's systems only ⌘ counts (Ctrl+E is "end of line" there). */
export function formatKeyItem(e: KeyboardEvent<HTMLTextAreaElement>): FormatItem | null {
  const mod = isApple() ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (!mod || e.altKey || e.nativeEvent.isComposing) return null;
  const letter = e.key.toLowerCase();
  return FORMAT_KEYS.find((k) => k.key === letter && k.shift === e.shiftKey)?.item ?? null;
}

let formatting = false;

/** True while a Format item is typing its change into a box: the box's change handler should take the text as it is. */
export const isFormatting = () => formatting;

const isHigh = (c: number) => c >= 0xd800 && c <= 0xdbff;
const isLow = (c: number) => c >= 0xdc00 && c <= 0xdfff;

/**
 * Applies an item to the textarea's selection. The change goes in as a browser edit of just the part that changes
 * (`insertText`), so Undo takes it back and the caret and the input event behave as for typing. Where the browser has
 * no such command, `setValue` gets the whole new text. Either way the same words stay selected.
 */
export function applyFormat(el: HTMLTextAreaElement, item: FormatItem, setValue: (value: string) => void): void {
  const old = el.value;
  const r = formatSelection(old, el.selectionStart, el.selectionEnd, item);
  if (r.text !== old) {
    // The part of the text that differs: keep the common start and end, but never cut a surrogate pair in two.
    let p = 0;
    const max = Math.min(old.length, r.text.length);
    while (p < max && old[p] === r.text[p]) p++;
    if (p > 0 && isHigh(old.charCodeAt(p - 1))) p--;
    let s = 0;
    while (s < max - p && old[old.length - 1 - s] === r.text[r.text.length - 1 - s]) s++;
    if (s > 0 && isLow(old.charCodeAt(old.length - s))) s--;
    const mid = r.text.slice(p, r.text.length - s);
    let done = false;
    formatting = true;
    try {
      el.focus();
      el.setSelectionRange(p, old.length - s);
      done = typeof document.execCommand === "function" && (mid ? document.execCommand("insertText", false, mid) : document.execCommand("delete"));
    } catch {
      done = false;
    } finally {
      formatting = false;
    }
    if (!done || el.value !== r.text) setValue(r.text);
  }
  el.setSelectionRange(r.start, r.end);
  // A render that follows can move the caret: put the selection back once it is done.
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(r.start, r.end);
  });
}

/** Handles a format key. Returns true when it did (the key is then used up). */
export function formatShortcut(e: KeyboardEvent<HTMLTextAreaElement>, setValue: (value: string) => void): boolean {
  const item = formatKeyItem(e);
  if (!item) return false;
  e.preventDefault();
  applyFormat(e.currentTarget, item, setValue);
  return true;
}
